import type { AnalysisItemData, PublicAnalysisCategoryData } from '@/features/analysisMenu/types';
import type { AnalysisHeaderData } from '@/features/analysisPage/types';
import { AnalysisStatisticsData } from '@/features/analysisPageStatistics/types';
import type {
  AnalysisStatus as BackendAnalysisStatus,
  AnalysisType as BackendAnalysisType,
  GetAnalysisByIdQuery,
  GetAnalysisStatsQuery,
  GetPublicAnalysisCategoriesQuery,
  GetUserAnalysesPaginatedQuery,
} from '@/shared/hooks/queries/generated';
import type { AnalysisStatus, AnalysisType } from '@/shared/types/analysis';

// Mapping des types de recherche backend vers frontend
const mapBackendAnalysisTypeToFrontendType = (analysisType: BackendAnalysisType): AnalysisType => {
  switch (analysisType) {
    case 'catalog':
      return 'product';
    case 'profile':
      return 'vendor';
    default:
      return 'product';
  }
};

// Mapping des statuts backend vers frontend
const mapBackendAnalysisStatusToFrontendStatus = (analysisStatus: BackendAnalysisStatus): AnalysisStatus => {
  switch (analysisStatus) {
    case 'starting':
      return 'starting';
    case 'active':
      return 'active';
    case 'inactive':
      return 'inactive';
    case 'error':
      return 'error';
    case 'removed':
      return 'removed';
    default:
      return 'starting';
  }
};

// Types dérivés des queries
type UserAnalysisItem = GetUserAnalysesPaginatedQuery['getUserAnalysesPaginated']['analyses'][0];
type PublicAnalysisItem = GetPublicAnalysisCategoriesQuery['getPublicAnalysisCategories'][0]['analyses'][0];
type AnalysisItem = GetAnalysisByIdQuery['getAnalysisById'];
type AnalysisWithStatsItem = GetAnalysisByIdQuery['getAnalysisById'];

// Type union pour tous les types d'analyse
type AnyAnalysisItem =
  | UserAnalysisItem
  | PublicAnalysisItem
  | NonNullable<AnalysisItem>
  | NonNullable<AnalysisWithStatsItem>;

// Mapper une catégorie publique d'analyse backend vers un item d'analyse frontend
export const mapPublicAnalysisCategoryToAnalysisCategoryItem = (
  analysis: GetPublicAnalysisCategoriesQuery['getPublicAnalysisCategories'][0],
): PublicAnalysisCategoryData => {
  return {
    id: analysis.id,
    name: analysis.name,
    analyses: analysis.analyses.map((item) => mapAnalysisToAnalysisItem(item)),
  };
};

// Mapper une analyse backend vers un item d'analyse frontend
export const mapAnalysisToAnalysisItem = (analysis: AnyAnalysisItem): AnalysisItemData => {
  return {
    id: analysis.id,
    name: analysis.name,
    type: mapBackendAnalysisTypeToFrontendType(analysis.type),
    status: mapBackendAnalysisStatusToFrontendStatus(analysis.status),
    createdAt: new Date(analysis.createdAt),
    endedAt: analysis.inactivatedAt ? new Date(analysis.inactivatedAt) : null,
    imageUrl: analysis.imageUrl ?? undefined,
    newSells: analysis.newSells ?? undefined,
    analysedProductCount: analysis.analysedProductCount ?? undefined,
  };
};

// Mapper une analyse backend vers les données d'en-tête d'analyse
export const mapAnalysisToAnalysisHeader = (analysis: AnyAnalysisItem): AnalysisHeaderData => {
  return {
    id: analysis.id,
    name: analysis.name,
    type: mapBackendAnalysisTypeToFrontendType(analysis.type),
    status: mapBackendAnalysisStatusToFrontendStatus(analysis.status),
    createdAt: new Date(analysis.createdAt),
    endedAt: analysis.inactivatedAt ? new Date(analysis.inactivatedAt) : null,
    imageUrl: analysis.imageUrl ?? undefined,
    pageUrl: analysis.pageUrl,
    isPublic: analysis.isPublic,
  };
};

// Fonction pour déterminer si une analyse est publique
export const isPublicAnalysis = (analysis: AnyAnalysisItem): boolean => {
  return analysis.isPublic;
};

// Fonction pour filtrer les analyses par type d'analyse
export const filterAnalysesByAnalysisType = (
  analyses: (UserAnalysisItem | PublicAnalysisItem)[],
  analysisType: AnalysisType,
): (UserAnalysisItem | PublicAnalysisItem)[] => {
  switch (analysisType) {
    case 'product':
      return analyses.filter((analysis) => analysis.type === 'catalog' && !analysis.isPublic);
    case 'vendor':
      return analyses.filter((analysis) => analysis.type === 'profile' && !analysis.isPublic);
    case 'public':
      return analyses.filter((analysis) => analysis.isPublic);
    default:
      return analyses;
  }
};

// Mapper les données statistiques d'une analyse
export const mapAnalysisStats = (stats: GetAnalysisStatsQuery['getAnalysisStats']): AnalysisStatisticsData => {
  return {
    averageSalePrice: stats.averageSalePrice ?? null,
    salePercentage: stats.salePercentage ?? null,
    averageSaleTimeSec: stats.averageSaleTimeSec ?? null,
    totalProductsAnalyzed: stats.totalProductsAnalyzed ?? 0,
    totalSalesRecorded: stats.totalSalesRecorded ?? 0,
    newProductsPerDay: stats.newProductsPerDay ?? null,
    salesPerDay: stats.salesPerDay ?? null,
    priceDistributionQ1: stats.priceDistributionQ1 ?? null,
    priceDistributionQ3: stats.priceDistributionQ3 ?? null,
    priceDistributionMin: stats.priceDistributionMin ?? null,
    priceDistributionMax: stats.priceDistributionMax ?? null,
    priceDistributionBuckets: stats.priceDistributionBuckets ?? null,
  };
};
